import { alpha, Box, styled, Typography } from '@mui/material';
import Button from 'components/Button';
import { AppAutocomplete } from 'components/FormElements';
import { ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '..';
import { ActionRefLabels } from 'further-ui/labels';
import { Row, Column } from 'components/Layout';

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.2),
  padding: theme.spacing(theme.spacingSizes.md),
  width: '100%',
  height: '146px',
}));

const FindMatchButton = styled(Button)(({ theme }) => ({
  ...theme.typography.body2,
  padding: 0,
  textDecoration: 'underline',
  color: theme.palette.primary.main,
}));

const NoTransactions = styled(Box)(({ theme }) => ({
  height: '146px',
  width: '100%',
  border: `1px solid ${theme.palette.grey[300]}`,
  borderTop: 'none',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'stretch',
}));

type Props = {
  onFindAndMatch: () => void;
  matchingReconcilableTransactions: Array<{
    id: string;
    unreconciledAmountTcy: number;
    counterpartyLabel: string;
    actionRef: ActionRef;
    currencyCode: CurrencyCode;
  }>;
};

const QuickReconcile: React.FC<Props> = ({
  matchingReconcilableTransactions,
  onFindAndMatch,
}) => {
  const { control } = useFormContext<FormValues>();

  if (!matchingReconcilableTransactions.length)
    return (
      <NoTransactions>
        <Row centered justify="center" fullWidth>
          <Button
            variant="outlined"
            size="large"
            onClick={() => onFindAndMatch()}
          >
            Find and match
          </Button>
        </Row>
      </NoTransactions>
    );

  const options = matchingReconcilableTransactions.map((transaction) => ({
    label: `${ActionRefLabels[transaction.actionRef]} - ${
      transaction.counterpartyLabel
    } - ${numberToCurrencyString(transaction.unreconciledAmountTcy, {
      currency: transaction.currencyCode,
    })}`,
    value: transaction.id,
  }));

  return (
    <>
      <Container>
        <Column spacing="sm">
          <Typography variant="h6">Quick reconcile</Typography>
          <Controller
            control={control}
            name="transactionId"
            render={({ field: { onChange, value } }) => (
              <AppAutocomplete
                disableClearable
                options={options}
                onChange={(_, value) => {
                  if (!value) return;
                  onChange(value.value);
                }}
                value={options.find((option) => option.value === value)}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.value}
                placeholder="Select transaction"
              />
            )}
          />
          <Typography variant="body2" color="textSecondary">
            or{' '}
            <FindMatchButton onClick={() => onFindAndMatch()}>
              Find a match
            </FindMatchButton>
          </Typography>
        </Column>
      </Container>
    </>
  );
};

export default QuickReconcile;
